import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { useDispatch } from "react-redux";
import dayjs from "@shared/dayjs";
import {
  BoxArchiveSolidV6,
  CheckSolidV6,
  EllipsisSolidV6,
  EyeSolidV6,
  LinkSolidV6,
  ShareSolidV6,
  SpinnerThirdSolidV6,
  TriangleExclamationSolidV6,
} from "@shared/v2/Icomoon";
import Button from "@shared/v2/Button";
import Checkbox from "@shared/v2/Checkbox";
import IconButton from "@shared/v2/IconButton";
import { Popover } from "@shared/v2/Popover";
import Typography from "@shared/v2/Typography";
import Dropdown from "@shared/v2/Dropdown";
import FieldLabel from "@shared/v2/FieldLabel";
import CollapsibleWebsiteCard from "../shared/CollapsibleWebsiteCard";
import { archivePage, getPage, getToken } from "../../reducers/websiteReducer/areaPageReducer";
import { setSnackbar } from "../../reducers/snackbarReducer";
import ArchivePageModal from "./ArchivePageModal";
import ShareItemModal from "../shared/ShareItemModal";
import {
  usePage,
  useSites,
  useToken,
  useTokenExpiresAt,
} from "../../reducers/websiteReducer/areaPageSelectors";
import { isTokenValid } from "./api";

const PublishPanel = ({
  data = undefined,
  hasChanges = false,
  isArchived = false,
  onSaveClicked,
  onPublishedClicked,
}) => {
  const loading = false;
  const pageSaved = false;
  const isScheduled = false;
  const sites = useSites();
  const page = usePage();
  const mainSite = sites?.find((s) => s.isMain) || sites?.[0];
  const siteDomain = (s) => `${s.subdomain ? `${s.subdomain}.` : ""}${s.domain}`;
  const pageUrl = mainSite ? `https://${siteDomain(mainSite)}/area${page?.path || ""}` : "";
  const [shareSite, setShareSite] = useState(null);
  const shareSiteOptions = (sites || []).map((s) => ({ label: siteDomain(s), value: s.uuid }));
  const selectedShareSite = shareSite || mainSite;
  const shareUrl = selectedShareSite
    ? `https://${siteDomain(selectedShareSite)}/area${page?.path || ""}`
    : "";

  const [showInNavigation, setShowInNavigation] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);
  const [sharePage, setSharePage] = useState(false);
  const dispatch = useDispatch();
  const token = useToken();
  const tokenExpiresAt = useTokenExpiresAt();
  // Archived is a page-level flag (isActive); refetch the page so the panel reflects it
  // without a refresh. Guarded by a ref (not state) so a re-entrant call in the same tick
  // still sees it, unlike a state flag a stale closure could miss.
  const archiving = useRef(false);
  const archive = async (isActive) => {
    if (archiving.current) return;
    archiving.current = true;
    try {
      // The editor can sit open past the token's 5 minutes; refresh before writing.
      if (!isTokenValid(token, tokenExpiresAt)) await dispatch(getToken()).unwrap();
      await dispatch(archivePage({ uuid: page?.uuid, isActive })).unwrap();
      await dispatch(getPage({ uuid: page?.uuid }));
    } finally {
      archiving.current = false;
    }
  };

  return (
    <CollapsibleWebsiteCard
      title="Publish"
      action={
        <>
          {!loading && !pageSaved && hasChanges && (
            <span className="tw-text-[#e5941b] tw-text-12d tw-flex tw-items-center tw-gap-[4px]">
              Unsaved changes <TriangleExclamationSolidV6 />
            </span>
          )}
          {loading && (
            <span className="tw-flex tw-items-center tw-gap-[8px]">
              Saving...
              <SpinnerThirdSolidV6 className="tw-animate-spin" />
            </span>
          )}
          {!loading && pageSaved && (
            <span className="tw-text-semantic-green-110 tw-text-12d tw-flex tw-items-center tw-gap-[4px]">
              Saved <CheckSolidV6 />
            </span>
          )}
        </>
      }
    >
      <div className="tw-flex tw-flex-col tw-gap-[24px]">
        <div className="tw-flex tw-flex-col tw-gap-[8px]">
          <div className="tw-flex tw-justify-between tw-items-center tw-gap-[4px]">
            <Typography variant="body-m" component="span">
              Status
            </Typography>

            {data?.status === "draft" && (
              <div className="tw-inline-flex tw-gap-[4px] tw-justify-center tw-items-center tw-w-[120px] tw-py-[4px] tw-px-[8px] tw-border-none tw-rounded-full tw-uppercase tw-text-11d tw-text-center tw-font-bold tw-text-white tw-bg-neutral-gray-50">
                Draft
              </div>
            )}

            {data?.status === "published" && (
              <div className="tw-inline-flex tw-gap-[4px] tw-justify-center tw-items-center tw-w-[120px] tw-py-[4px] tw-px-[8px] tw-border-none tw-rounded-full tw-uppercase tw-text-11d tw-text-center tw-font-bold tw-text-white tw-bg-theme-primary">
                Published
              </div>
            )}

            {isArchived && (
              <div className="tw-inline-flex tw-gap-[4px] tw-justify-center tw-items-center tw-w-[120px] tw-py-[4px] tw-px-[8px] tw-border-none tw-rounded-full tw-uppercase tw-text-11d tw-text-center tw-font-bold tw-text-white w-bg-theme-primary tw-bg-gray-99">
                Archived
              </div>
            )}
          </div>
          {!isScheduled && (
            <div className="tw-flex tw-justify-between tw-items-center tw-gap-[4px]">
              <Typography variant="body-m" component="span">
                {isArchived || !data?.isPublished ? "Last " : ""}Published
              </Typography>
              <Typography variant="body-s" className="!tw-text-gray-50">
                {data?.datePublished ? dayjs(data.datePublished).format("MM/DD/YYYY [at] hh:mm a") : "—"}
              </Typography>
            </div>
          )}
          {isScheduled && (
            <div className="tw-flex tw-justify-between tw-items-center tw-gap-[4px]">
              <Typography variant="body-m" component="span">
                Scheduled For
              </Typography>
              <Typography variant="body-s" className="!tw-text-gray-50">
                {dayjs(data?.dateToPublish).format("MM/DD/YYYY [at] hh:mm a")}
              </Typography>
            </div>
          )}
          <div className="tw-flex tw-justify-between tw-items-center tw-gap-[4px]">
            <Typography variant="body-m" component="span">
              Last Modified
            </Typography>
            <Typography variant="body-s" className="!tw-text-gray-50">
              {data?.dateUpdated ? dayjs(data?.dateUpdated).format("MM/DD/YYYY") : "—"}
            </Typography>
          </div>
          {isArchived && (
            <div className="tw-flex tw-justify-between tw-items-center tw-gap-[4px]">
              <Typography variant="body-m" component="span">
                Archived
              </Typography>
              {/* No archived-date field exists on the backend yet. */}
              <Typography variant="body-s" className="!tw-text-gray-50">
                —
              </Typography>
            </div>
          )}
          <div className="tw-flex tw-justify-between tw-items-center tw-gap-[4px]">
            <Typography variant="body-m" component="span">
              Show in Navigation
            </Typography>
            <Checkbox checked={showInNavigation} onChange={() => setShowInNavigation(!showInNavigation)} />
          </div>
        </div>

        <div className="tw-flex tw-gap-[4px]">
          <Popover placement="bottom-end">
            <Popover.Trigger>
              <IconButton schema="secondary" size="medium" className="tw-flex-none tw-w-[36px] tw-h-[36px]">
                <EllipsisSolidV6 size="l" />
              </IconButton>
            </Popover.Trigger>
            <Popover.Content>
              {!isArchived && (
                <Popover.Item
                  icon={<ShareSolidV6 />}
                  onClick={() => {
                    setSharePage(true);
                  }}
                  disabled={!data?.isPublished}
                >
                  Share
                </Popover.Item>
              )}

              {!isArchived && (
                <Popover.Item
                  icon={<LinkSolidV6 />}
                  onClick={() =>
                    window.copyTextToClipboard(pageUrl).then(() =>
                      dispatch(
                        setSnackbar({
                          show: true,
                          position: "top",
                          text: "Link copied",
                          schema: "executed",
                          timeout: 5000,
                          closeOnTimeout: () => dispatch(setSnackbar({ show: false })),
                        }),
                      ),
                    )
                  }
                  disabled={!data?.isPublished}
                >
                  Copy Link
                </Popover.Item>
              )}

              {!isArchived && (
                <Popover.Item
                  icon={<EyeSolidV6 />}
                  onClick={() => window.open(pageUrl, "_blank")}
                  disabled={!data?.isPublished}
                >
                  View Page
                </Popover.Item>
              )}

              {!isArchived && (
                <Popover.Item
                  icon={<BoxArchiveSolidV6 />}
                  onClick={() => setArchiveModalOpen(true)}
                  // Archiving refetches the page, which would silently drop unsaved edits.
                  disabled={hasChanges}
                >
                  Archive
                </Popover.Item>
              )}

              {isArchived && (
                <Popover.Item
                  icon={<BoxArchiveSolidV6 />}
                  onClick={() => archive(true)}
                  disabled={hasChanges}
                >
                  Unarchive
                </Popover.Item>
              )}
            </Popover.Content>
          </Popover>
          <Button schema="secondary" size="medium" onClick={() => onSaveClicked()} disabled={!hasChanges}>
            Save
          </Button>
          {/* <Button schema="primary" size="medium" onClick={() => onPublishedClicked()} disabled={!hasChanges}>
            {data?.isPublished ? "Unpublish" : "Publish"}
          </Button> */}
          <Button schema="primary" size="medium" onClick={() => onPublishedClicked()}>
            Publish
          </Button>
        </div>
      </div>
      <ArchivePageModal
        open={archiveModalOpen}
        onClose={() => setArchiveModalOpen(false)}
        onConfirm={() => {
          archive(false);
          setArchiveModalOpen(false);
        }}
      />
      <ShareItemModal
        title="Share Page"
        item={sharePage}
        onClose={() => {
          setSharePage(null);
          setShareSite(null);
        }}
        url={shareUrl}
        action={
          <>
            <FieldLabel label="Which site would you like to send viewers to" />
            <Dropdown
              options={shareSiteOptions}
              value={shareSiteOptions.find((o) => o.value === selectedShareSite?.uuid) || null}
              onChange={(o) => setShareSite(sites?.find((s) => s.uuid === o.value) || null)}
            />
          </>
        }
      />
    </CollapsibleWebsiteCard>
  );
};

PublishPanel.propTypes = {
  data: PropTypes.shape({
    status: PropTypes.string,
    isPublished: PropTypes.bool,
    datePublished: PropTypes.string,
    dateToPublish: PropTypes.string,
    dateUpdated: PropTypes.string,
  }),
  hasChanges: PropTypes.bool,
  isArchived: PropTypes.bool,
  onSaveClicked: PropTypes.func.isRequired,
  onPublishedClicked: PropTypes.func.isRequired,
};

export default PublishPanel;
